import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/pinia/auth'

// Confirmed with BE 2026-09-04: every request to the gateway made while in an org-flow session
// (the shared `digitalbuildings` realm) needs an `Organization-ID` header, on every service, not
// just core-service -- without it, requests 400. Read straight from localStorage rather than
// `useOrganizationStore()` to avoid a circular import (pinia/organization.ts ->
// services/organizations.service.ts -> services/api.service.ts -> this file); the store already
// persists the key under this exact key on every selection (see setCurrentOrganization). Empty
// for every normal per-customer realm-flow session, so this is a no-op there. Two OTHER entry
// points that also talk to the gateway need the same header (added separately, same pattern):
// src/http-client/models/HttpClient.axios.ts and src/composables/useFetch.ts's
// createAxiosClient(). HTTPKeyCloakClient (Keycloak itself) and HTTPFileServiceClient (a separate
// deployment via VITE_FILE_SERVICE_URL, bypassing the gateway) are the only two clients here that
// deliberately do NOT get it.
function withOrganizationHeader(headers: Record<string, unknown>) {
  const organizationKey = localStorage.getItem('organizationKey')
  if (organizationKey) headers['Organization-ID'] = organizationKey
  return headers
}

const HTTPClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL,
  timeout: 30000
})

const HTTPGatewayClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL,
  timeout: 30000
})

const HTTPKeyCloakClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL,
  timeout: 30000
})

const HTTPFileClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL + '/file',
  timeout: 30000
})

// Dedicated client for the file service (separate deployment from the API gateway).
// Base URL is configured via VITE_FILE_SERVICE_URL (e.g. https://file.digitalbuildings.xyz).
// Used for all file uploads to bypass the API gateway's request timeout limits.
const HTTPFileServiceClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_FILE_SERVICE_URL,
  timeout: 0
})

HTTPClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore()
    config.headers = config.headers || {}
    config.headers['Accept'] = 'application/json'
    config.headers['Content-Type'] = 'application/json'
    config.headers['Authorization'] = `Bearer ${accessToken}`
    withOrganizationHeader(config.headers)
    return config
  },
  async (err: Error) => {
    throw err
  }
)

HTTPGatewayClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore()
    config.headers = config.headers || {}
    config.headers['Accept'] = config.headers['Accept'] || 'application/json'
    config.headers['Authorization'] = `Bearer ${accessToken}`
    withOrganizationHeader(config.headers)
    return config
  },
  async (err: Error) => {
    throw err
  }
)

HTTPFileClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore()
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${accessToken}`
    withOrganizationHeader(config.headers)
    return config
  },
  async (err: Error) => {
    throw err
  }
)

// Goes to VITE_FILE_SERVICE_URL, a separate deployment that bypasses the gateway entirely -- no
// Organization-ID header here (confirmed 2026-09-04, unlike the other four clients above, which
// all talk to the gateway).
HTTPFileServiceClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore()
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${accessToken}`
    return config
  },
  async (err: Error) => {
    throw err
  }
)

HTTPKeyCloakClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore()
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${accessToken}`
    return config
  },
  async (err: Error) => {
    throw err
  }
)

export { HTTPClient, HTTPFileClient, HTTPGatewayClient, HTTPFileServiceClient, HTTPKeyCloakClient }
